import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Map as MapIcon, Download, Eye, Layers } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";

export default function MapsPage() {
  const { t, tx, lang } = useI18n();
  const [maps, setMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLang, setFilterLang] = useState("");

  useEffect(() => {
    setLoading(true);
    const opts = {};
    if (filterLang) opts.language = filterLang;
    api.get("/maps", { params: opts })
      .then(r => setMaps(r.data))
      .catch(() => setMaps([]))
      .finally(() => setLoading(false));
  }, [filterLang]);

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 md:py-16">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-[hsl(var(--primary-green))] text-white flex items-center justify-center">
          <Layers className="w-6 h-6" />
        </div>
        <div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold tracking-tight">{t.nav.maps}</h1>
          <p className="text-muted-foreground">Tourist maps & route maps</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={() => setFilterLang("")}
          data-testid="maps-lang-all"
          className={`px-4 py-2 rounded-full text-sm font-medium transition ${!filterLang ? "bg-foreground text-white" : "bg-muted"}`}
        >
          {t.common.viewAll}
        </button>
        {["es", "en", "fr"].map(l => (
          <button
            key={l}
            onClick={() => setFilterLang(l)}
            data-testid={`maps-lang-${l}`}
            className={`px-4 py-2 rounded-full text-sm font-medium uppercase transition ${
              filterLang === l ? "bg-[hsl(var(--primary-green))] text-white" : "bg-muted"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {loading ? (
          <p className="text-muted-foreground">{t.common.loading}</p>
        ) : maps.length === 0 ? (
          <div className="py-20 text-center" data-testid="maps-empty">
            <MapIcon className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">{t.common.noResults}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="maps-grid">
            {maps.map(m => (
              <div key={m.id} data-testid={`map-card-${m.slug}`} className="group bg-card border border-border rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-all">
                <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                  {m.map_type === "image" ? (
                    <img src={fileUrl(m.url)} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[hsl(var(--primary-green))]/20 to-[hsl(var(--primary-red))]/10">
                      <MapIcon className="w-16 h-16 text-[hsl(var(--primary-green))]" />
                    </div>
                  )}
                  <span className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white text-xs font-semibold uppercase">{m.extension}</span>
                </div>
                <div className="p-5">
                  <h3 className="font-display text-lg font-semibold leading-tight tracking-tight">{tx(m.title)}</h3>
                  {tx(m.description) && (
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{tx(m.description)}</p>
                  )}
                  <div className="mt-4 flex items-center gap-2">
                    <a
                      href={fileUrl(m.url)}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`map-view-${m.slug}`}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-[hsl(var(--primary-green))] text-white text-sm font-semibold hover:bg-[hsl(var(--primary-green-hover))] transition"
                    >
                      <Eye className="w-4 h-4" /> View
                    </a>
                    <a
                      href={fileUrl(m.url)}
                      download
                      data-testid={`map-download-${m.slug}`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-muted text-sm font-semibold hover:bg-muted/70 transition"
                    >
                      <Download className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
